import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackGA } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Check, Sparkles, Lock, ShieldCheck, ArrowRight, Loader2, AlertCircle,
  X, PauseCircle, Tag, Headphones, ArrowLeft, HelpCircle, Flame, CreditCard,
} from "lucide-react";
import { Logo } from "@/components/Logo";

const POLL_MAX = 12;
const POLL_INTERVAL = 2500;

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const pollRef = useRef({ active: false, count: 0 });

  const sessionId = params.get("session_id");

  const loadAll = async () => {
    setLoading(true);
    // Use allSettled so a failure on one endpoint (e.g. /billing/me 401 right
    // after redeploy when the session cookie hasn't synced yet) doesn't blank
    // out the plans card too. Each side updates independently.
    const [p, b] = await Promise.allSettled([api.get("/billing/plans"), api.get("/billing/me")]);
    if (p.status === "fulfilled") setPlans(p.value.data.plans || []);
    if (b.status === "fulfilled") setBilling(b.value.data);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Poll checkout status after Stripe redirect
  useEffect(() => {
    if (!sessionId || pollRef.current.active) return;
    pollRef.current = { active: true, count: 0 };

    const finish = async (subscription, amountTotalCents) => {
      toast.success("Payment confirmed! You're all set.");
      // Pass the actual dollar amount the customer just paid (Stripe returns
      // it as cents in `amount_total`) so Meta/GA4 can attribute true revenue
      // to the ad/source — critical for accurate CAC and ROAS reporting.
      const valueUsd = amountTotalCents != null ? Number(amountTotalCents) / 100 : 1;
      trackGA(
        "subscription_active",
        { value: valueUsd, currency: "USD", plan_id: subscription?.plan_id || "early_adopter" },
        { once: true },
      );
      pollRef.current.active = false;
      params.delete("session_id");
      setParams(params, { replace: true });
      await loadAll();
      try {
        const { data: meCompany } = await api.get("/companies/me");
        if (!meCompany.onboarding_completed) {
          setTimeout(() => nav("/onboarding"), 600);
        }
      } catch {}
    };

    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await finish(data.subscription, data.amount_total);
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          pollRef.current.active = false;
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        if (pollRef.current.count++ < POLL_MAX) {
          setTimeout(poll, POLL_INTERVAL);
          return;
        }
        // Polling timed out — check if the webhook already activated us.
        try {
          const { data: meBilling } = await api.get("/billing/me");
          const status = meBilling?.subscription?.status;
          if (status === "trialing" || status === "active") {
            await finish(meBilling.subscription, null);
            return;
          }
        } catch {}
        pollRef.current.active = false;
        toast.error("We couldn't confirm your payment in time. Refresh in a few seconds — your subscription is likely active.", { duration: 10000 });
        params.delete("session_id");
        setParams(params, { replace: true });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("checkout status poll failed:", e);
        // Don't give up — webhook may have already done the work. Recheck /billing/me.
        try {
          const { data: meBilling } = await api.get("/billing/me");
          const status = meBilling?.subscription?.status;
          if (status === "trialing" || status === "active") {
            await finish(meBilling.subscription);
            return;
          }
        } catch {}
        if (pollRef.current.count++ < POLL_MAX) {
          setTimeout(poll, POLL_INTERVAL);
        } else {
          pollRef.current.active = false;
          toast.error("Couldn't reach Stripe to confirm. Refresh in a moment — your subscription is likely active.", { duration: 10000 });
          params.delete("session_id");
          setParams(params, { replace: true });
        }
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startCheckout = async (phase) => {
    setActionLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: "early_adopter",
        phase,
        origin_url: window.location.origin,
      });
      trackGA("trial_checkout_started", { plan_id: "early_adopter", phase });
      window.location.href = data.url;
    } catch (e) {
      // Surface the real error so we can debug live issues without DevTools.
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = detail
        ? `${status ? `[${status}] ` : ""}${typeof detail === "string" ? detail : JSON.stringify(detail)}`
        : (e.message || "Couldn't start checkout. Try again.");
      // Long error: open in a console.log too so the user can copy it.
      // eslint-disable-next-line no-console
      console.error("Checkout failed:", { status, detail, error: e });
      toast.error(msg, { duration: 10000 });
      setActionLoading(false);
    }
  };

  const [showCancelFlow, setShowCancelFlow] = useState(false);

  const cancel = async (reason, offered) => {
    try {
      const { data } = await api.post("/billing/cancel", { reason, offered });
      if (data.scheduled) {
        toast.success("Subscription will cancel at end of your current billing period. You keep full access until then.");
      } else {
        toast.success("Subscription canceled");
      }
      setShowCancelFlow(false);
      loadAll();
    } catch {
      toast.error("Couldn't cancel");
    }
  };

  const acceptSave = async (offer, reason) => {
    try {
      await api.post("/billing/retention/save", { offer, reason });
      const success = {
        pause: "Subscription paused for 30 days. Welcome back any time.",
        discount: "Discount applied — your next 3 months are 50% off.",
        support: "Got it. Someone from our team will reach out within 1 business day.",
      };
      toast.success(success[offer] || "Thanks for staying!");
      setShowCancelFlow(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't apply that offer. Please contact support.");
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/billing" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open billing portal");
      setPortalLoading(false);
    }
  };

  const reactivate = async () => {
    setActionLoading(true);
    try {
      await api.post("/billing/reactivate", {});
      toast.success("Auto-renewal restored — you'll keep your subscription.");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reactivate");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <Centered><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></Centered>;
  }

  const sub = billing?.subscription || { status: "inactive" };
  const plan = plans[0] || billing?.plan;
  const status = sub.status;
  const isActive = status === "active" || status === "trialing";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <Logo size={36} withWordmark />
          </Link>
          {user && (
            <button onClick={async () => { await logout(); nav("/login"); }} data-testid="billing-signout" className="text-sm text-slate-500 hover:text-slate-900">
              Sign out
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 lg:py-16">
        {sessionId && pollRef.current.active && (
          <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <div className="text-sm text-blue-900">Confirming your payment with Stripe…</div>
          </div>
        )}

        {/* Status card */}
        {isActive ? (
          <div className="rounded-3xl bg-emerald-50 border border-emerald-200 p-5 sm:p-6 mb-8" data-testid="billing-active-card">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-slate-900 text-base sm:text-lg leading-tight">
                  {status === "trialing" ? "Your 30-day trial is active" : "Your subscription is active"}
                </div>
                <div className="text-sm text-slate-600 mt-1.5 leading-snug">
                  Plan: <strong>{plan?.name}</strong>
                  <span className="block sm:inline sm:before:content-['_·_']">
                    {status === "trialing"
                      ? `Trial ends ${formatDate(sub.trial_end)}`
                      : `Next bill ${formatDate(sub.current_period_end)} · $${plan?.monthly_amount}/mo`}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                onClick={() => setShowCancelFlow(true)}
                className="text-xs text-slate-500 hover:text-red-600 underline self-start sm:self-auto"
                data-testid="billing-cancel-btn"
              >
                Cancel subscription
              </button>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  onClick={openPortal}
                  disabled={portalLoading || !sub.stripe_customer_id}
                  variant="outline"
                  className="rounded-xl h-11 px-4 border-slate-300 hover:border-slate-500"
                  data-testid="billing-manage-btn"
                  title={!sub.stripe_customer_id ? "Available once you finish signup checkout" : "Update card, view invoices, cancel"}
                >
                  {portalLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
                  Manage billing
                </Button>
                <Button
                  onClick={() => nav("/dashboard")}
                  className="rounded-xl bg-[#0F172A] hover:bg-black h-11 px-5 w-full sm:w-auto"
                  data-testid="billing-go-dashboard"
                >
                  Open dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
            {sub.cancel_at_period_end && (
              <div className="mt-4 pt-4 border-t border-emerald-200/70 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="text-amber-700 font-semibold">
                  ⚠️ Cancels {formatDate(sub.current_period_end)} · service ends then
                </div>
                <Button
                  onClick={reactivate}
                  disabled={actionLoading}
                  size="sm"
                  className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                  data-testid="billing-reactivate-btn"
                >
                  Keep my subscription
                </Button>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-emerald-200/70">
              <button
                onClick={() => nav("/billing/history")}
                className="text-xs font-bold text-[#C75D33] hover:text-[#A84C28] inline-flex items-center gap-1.5"
                data-testid="billing-view-history-btn"
              >
                View lead-billing history & wallet movements <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        ) : status === "canceled" ? (
          <div className="rounded-3xl bg-amber-50 border border-amber-200 p-6 mb-8 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold text-slate-900">Subscription canceled</div>
              <div className="text-sm text-slate-600 mt-1">Reactivate anytime to keep collecting quotes.</div>
            </div>
          </div>
        ) : null}

        {/* Pricing offer */}
        {!isActive && plan && (
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-br from-[#0F172A] to-black p-6 sm:p-10 text-center text-white">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/95 text-amber-950 text-[11px] font-extrabold uppercase tracking-widest mb-5">
                <Sparkles className="w-3.5 h-3.5" /> Early Adopter Offer
              </div>

              {/* HERO: $1 is the star */}
              <div className="flex items-baseline justify-center gap-2 leading-none">
                <span className="font-display text-7xl sm:text-8xl font-extrabold tracking-tight drop-shadow-sm">$1</span>
              </div>
              <div className="font-display text-xl sm:text-2xl font-bold mt-3">
                Try WindowScreenQuote for 30 days
              </div>
              <p className="text-white/85 mt-3 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                Launch your white-label instant-quote funnel today. Start collecting leads in the next 10 minutes.
              </p>

              {/* The $97 is now small, supporting evidence — not the hero */}
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs text-white/85">
                After trial: <strong className="text-white">${plan.monthly_amount}/mo</strong> · locked in for life · cancel anytime
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <ul className="space-y-2.5 text-sm text-slate-700">
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> Your own hosted instant-quote funnel</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> Unlimited quotes and leads</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> White-label branding (logo, colors)</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> Lead pipeline + email notifications</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> 3 lead-capture modes (Max / High Intent / Hybrid)</li>
                <li className="flex gap-2.5"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> Grandfathered pricing — never goes up</li>
              </ul>

              <Button
                onClick={() => startCheckout("trial")}
                disabled={actionLoading}
                className="w-full mt-7 h-14 rounded-2xl bg-[#0F172A] hover:bg-black text-base font-bold shadow-lg shadow-[#C75D33]/25"
                data-testid="billing-start-trial-btn"
              >
                {actionLoading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Redirecting…</> : <>Start my $1 trial <ArrowRight className="w-5 h-5 ml-1.5" /></>}
              </Button>

              <div className="text-center text-xs text-slate-500 mt-4 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure checkout by Stripe · Cancel anytime
              </div>
              <div className="text-center text-[11px] text-slate-400 mt-3 leading-relaxed max-w-xs mx-auto">
                If WindowScreenQuote helps you close just one extra job a month, it pays for itself many times over.
              </div>
            </div>
          </div>
        )}

        {!plan && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="text-sm text-amber-900">Billing isn't configured yet. Ask the platform administrator to set up Stripe.</div>
          </div>
        )}
      </main>

      <RetentionDialog
        open={showCancelFlow}
        onOpenChange={setShowCancelFlow}
        plan={plan}
        onAcceptSave={acceptSave}
        onConfirmCancel={cancel}
      />
    </div>
  );
}

/* ---------- Retention save flow ----------
 * Step 1: ask reason (each reason maps to a tailored save offer)
 * Step 2: show the tailored offer + "yes, save me" / "no, still cancel"
 * Step 3: final "are you sure?" with the cost they're walking away from
 */

const REASONS = [
  { id: "too_expensive",  label: "Too expensive right now",       offer: "discount" },
  { id: "not_using",      label: "Not using it enough",            offer: "pause" },
  { id: "hard_to_use",    label: "Hard to set up / use",           offer: "support" },
  { id: "not_enough_leads", label: "Not getting enough leads",     offer: "support" },
  { id: "switched",       label: "Switched to another tool",       offer: "discount" },
  { id: "closing_business", label: "Closing or pausing my business", offer: "pause" },
  { id: "other",          label: "Something else",                  offer: "support" },
];

const OFFER_META = {
  pause: {
    icon: PauseCircle,
    eyebrow: "Take a break, not a goodbye",
    title: "Pause your subscription for 30 days — free",
    desc:
      "Keep your funnel, branding, and leads exactly where they are. Billing pauses for the next 30 days, your funnel goes into a 'we'll be right back' state, and we automatically reactivate you when you're ready to take leads again.",
    cta: "Pause for 30 days",
    badge: "Most flexible",
  },
  discount: {
    icon: Tag,
    eyebrow: "Stay another quarter — half off",
    title: "Get 50% off your next 3 months",
    desc:
      "Founder rate is already locked at $97/mo for life — this knocks it down to $48.50/mo for the next 3 months. If WindowScreenQuote closes you just one extra job this quarter, the math is impossible to lose.",
    cta: "Apply 50% off — 3 months",
    badge: "Best value",
  },
  support: {
    icon: Headphones,
    eyebrow: "Let us help, not lose you",
    title: "Free 20-minute 1:1 with our team",
    desc:
      "We'll walk through your funnel, your pricing, and your lead flow live — and ship any tweaks that night. Most pros find at least one fix in 20 minutes that more than pays for the next 12 months.",
    cta: "Book a free 1:1",
    badge: "We come to you",
  },
};

function RetentionDialog({ open, onOpenChange, plan, onAcceptSave, onConfirmCancel }) {
  const [step, setStep] = useState("reason");
  const [reason, setReason] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("reason");
      setReason(null);
    }
  }, [open]);

  const offerKey = reason ? REASONS.find((r) => r.id === reason)?.offer : null;
  const offer = offerKey ? OFFER_META[offerKey] : null;
  const monthly = plan?.monthly_amount || 97;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg w-[calc(100%-2rem)] rounded-3xl p-0 overflow-hidden border-slate-200"
        data-testid="retention-dialog"
      >
        {step === "reason" && (
          <div className="p-6 sm:p-8">
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-2">Before you go</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
              Quick question — what's making you cancel?
            </h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Tell us why and we'll see if there's a way to make this work for you. No sales pitch — just one screen.
            </p>
            <div className="mt-5 space-y-2">
              {REASONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => { setReason(r.id); setStep("offer"); }}
                  data-testid={`retention-reason-${r.id}`}
                  className="w-full text-left px-4 py-3.5 rounded-xl border border-slate-200 hover:border-[#C75D33] hover:bg-[#C75D33]/[0.04] transition-colors flex items-center justify-between gap-3"
                >
                  <span className="text-sm font-semibold text-slate-800">{r.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-5 text-xs text-slate-500 hover:text-slate-800 underline"
              data-testid="retention-close"
            >
              Never mind, take me back
            </button>
          </div>
        )}

        {step === "offer" && offer && (
          <div>
            <div className="bg-gradient-to-br from-[#0F172A] to-black text-white p-6 sm:p-8 relative">
              <div className="absolute top-4 right-4">
                <button onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white" data-testid="retention-offer-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C75D33] text-white text-[10px] font-extrabold uppercase tracking-widest mb-3">
                <Sparkles className="w-3 h-3" /> {offer.badge}
              </div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">{offer.eyebrow}</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight mt-1.5">
                {offer.title}
              </h2>
              <div className="mt-4 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10">
                <offer.icon className="w-6 h-6 text-[#E2A878]" />
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-sm text-slate-700 leading-relaxed">{offer.desc}</p>
              <div className="mt-5 grid gap-2.5">
                <Button
                  onClick={() => onAcceptSave(offerKey, reason)}
                  className="w-full h-12 rounded-2xl bg-[#0F172A] hover:bg-black text-base font-bold"
                  data-testid="retention-accept-offer"
                >
                  {offer.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep("confirm")}
                  className="w-full h-11 rounded-2xl border-slate-200 text-slate-600 hover:text-slate-900 text-sm font-semibold"
                  data-testid="retention-decline-offer"
                >
                  No thanks, still cancel
                </Button>
              </div>
              <button
                onClick={() => setStep("reason")}
                className="mt-4 text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> Pick a different reason
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="p-6 sm:p-8">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
              <Flame className="w-6 h-6" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
              You're about to lose your founder rate.
            </h2>
            <p className="text-sm text-slate-700 mt-3 leading-relaxed">
              Cancel now and you give up <strong>${monthly}/mo locked in forever</strong>. When you come back, you'll pay whatever the public rate is at that time — and we expect that to be at least <strong>${monthly + 100}/mo</strong> once the marketplace launches.
            </p>
            <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-2">
              <Row icon={X} label="You'll lose the $97/mo grandfathered rate" />
              <Row icon={X} label="Your quote funnel will go offline" />
              <Row icon={X} label="No more hot-lead alerts (email + text)" />
              <Row icon={X} label="You'll lose priority access to local marketplace leads" />
            </div>
            <div className="mt-6 grid gap-2.5">
              <Button
                onClick={() => onOpenChange(false)}
                className="w-full h-12 rounded-2xl bg-[#0F172A] hover:bg-black text-base font-bold"
                data-testid="retention-keep-active"
              >
                <HelpCircle className="w-4 h-4 mr-1.5" /> Keep my subscription
              </Button>
              <button
                onClick={() => onConfirmCancel(REASONS.find((r) => r.id === reason)?.label || reason, offerKey)}
                className="w-full h-10 text-xs text-slate-500 hover:text-red-600 underline"
                data-testid="retention-final-cancel"
              >
                I understand — cancel anyway
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label }) {
  return (
    <div className="flex items-start gap-2 text-sm text-slate-700">
      <Icon className="w-3.5 h-3.5 text-rose-500 mt-1 shrink-0" />
      <span>{label}</span>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center">{children}</div>;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}
